import { LoadStatus } from '@servicetitan/cp-ui';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { CustomersApi, SchedulerApi } from 'api';
import { observer } from 'mobx-react';
import { FC, useEffect } from 'react';
import { DynamicSchedulerType, SchedulerStore } from '../../stores/scheduler.store';
import { SchedulingProProvider } from './scheduling-pro-provider';
import { SchedulingProWidget } from './scheduling-pro-widget';
import { WebSchedulerProvider } from './web-scheduler-provider';
import { WebSchedulerWidget } from './web-scheduler-widget';

interface SchedulingWrapperProps {
    /** Render the x-small neutral "Book Us" used by the non-sidebar header. */
    compact?: boolean;
}

export const SchedulingWrapper: FC<SchedulingWrapperProps> = provide({
    singletons: [SchedulerApi, CustomersApi, SchedulerStore],
})(
    observer(({ compact }: SchedulingWrapperProps) => {
        const [{ loadSchedulerSettings, loadStatus, scheduler }] = useDependencies(SchedulerStore);

        useEffect(() => {
            if (loadStatus === LoadStatus.None && !scheduler) {
                loadSchedulerSettings();
            }
        }, [loadSchedulerSettings, loadStatus, scheduler]);

        if (loadStatus !== LoadStatus.Ok || !scheduler) {
            return null;
        }
        if (scheduler.type === DynamicSchedulerType.None) {
            return null;
        }
        return scheduler.type === DynamicSchedulerType.SchedulingPro
            ? scheduler.id && scheduler.dataApiKey && scheduler.schedulingProEmbedScriptUrl && (
                  <SchedulingProProvider
                      dataApiKey={scheduler.dataApiKey}
                      schedulerId={scheduler.id.toString()}
                      scriptUrl={scheduler.schedulingProEmbedScriptUrl}
                  >
                      <SchedulingProWidget compact={compact} />
                  </SchedulingProProvider>
              )
            : scheduler.publicCode && scheduler.webSchedulerScriptUrl && (
                  <WebSchedulerProvider
                      publicCode={scheduler.publicCode}
                      scriptUrl={scheduler.webSchedulerScriptUrl}
                  >
                      <WebSchedulerWidget compact={compact} />
                  </WebSchedulerProvider>
              );
    })
);
